import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X, BookOpen, RotateCcw, FileText, ChevronDown, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { sanitizeHtml } from "../lib/sanitize";
import { toast } from "sonner";

/**
 * Help button + Accordion popover with route-contextual handbook sections.
 *
 * Pulls live handbook sections from /api/handbook/sections per role + current
 * route. Includes a "Komplettes Handbuch (PDF)" download.
 */

const ROLE_LABEL = {
  verkaufer: "Vertriebler",
  manager: "Manager",
  direktor: "Direktor",
};

// Map any legacy roles to one of the 3 supported handbook roles
const normalizeRole = (r) => {
  if (r === "admin") return "direktor";
  if (r === "trainer") return "manager";
  if (r === "manager" || r === "direktor" || r === "verkaufer") return r;
  return "verkaufer";
};

const HelpButton = ({ onReopenTour }) => {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const role = normalizeRole(user?.role);

  const loadSections = useCallback(async () => {
    if (!role) return;
    setLoading(true);
    try {
      const { data } = await api.get("/handbook/sections", {
        params: { role, route: location.pathname },
      });
      setSections(data.sections || []);
      // Auto-expand the first matched section so user sees content immediately
      if (data.sections?.length > 0) setExpandedId(data.sections[0].id);
    } catch (e) {
      console.warn("Failed to load handbook sections", e);
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [role, location.pathname]);

  useEffect(() => {
    if (open) loadSections();
  }, [open, loadSections]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const reopenTour = async () => {
    try {
      await api.post("/me/onboarding/reset");
      setOpen(false);
      onReopenTour?.();
    } catch (e) {
      console.warn("Failed to reset onboarding", e);
      toast.error("Tour konnte nicht neu gestartet werden");
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${baseUrl}/api/handbook/${role}.pdf`;
      // Open in new tab — browser handles PDF display + download
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error("PDF konnte nicht geöffnet werden");
    } finally {
      setTimeout(() => setPdfLoading(false), 1200);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-novatus-green/40 bg-novatus-green/10 text-novatus-green hover:bg-novatus-green hover:text-black hover:border-novatus-green transition-all hover:scale-[1.03] active:scale-100"
        aria-label="Hilfe öffnen"
        data-testid="help-btn"
      >
        <HelpCircle className="w-5 h-5" strokeWidth={2.5} />
        <span className="hidden sm:inline text-xs font-bold tracking-wider uppercase">Hilfe</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[55] flex items-start sm:items-center justify-center p-4 pt-6 bg-black/80 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          role="dialog"
          aria-modal="true"
          data-testid="help-popover"
        >
          <div
            className="relative w-full max-w-lg max-h-[calc(100dvh-3rem)] sm:max-h-[85vh] overflow-hidden nv-card flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="help-dialog-content"
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-3 border-b border-novatus shrink-0">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-novatus-green" />
                  <span className="label-eyebrow">Schnellhilfe · {ROLE_LABEL[role] || "Vertriebler"}</span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  type="button"
                  className="text-novatus-text-3 hover:text-white -mr-2 p-3 rounded-full hover:bg-white/5 active:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Schließen"
                  data-testid="help-close-top-btn"
                >
                  <X className="w-5 h-5" strokeWidth={2.5} />
                </button>
              </div>
              <h3 className="font-display font-bold text-lg text-white mt-2" data-testid="help-title">
                Hilfe für diesen Bereich
              </h3>
              <p className="text-xs text-novatus-text-3 mt-1">
                {sections.length > 0
                  ? `${sections.length} Abschnitt${sections.length !== 1 ? "e" : ""} relevant zu dieser Seite`
                  : "Hilfetexte werden geladen..."}
              </p>
            </div>

            {/* Accordion Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="help-sections">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-novatus-green" />
                </div>
              ) : sections.length === 0 ? (
                <p className="text-sm text-novatus-text-3 py-4 text-center">
                  Für diesen Bereich liegen noch keine Hilfetexte vor.
                </p>
              ) : (
                <div className="space-y-2">
                  {sections.map((s) => {
                    const isOpen = expandedId === s.id;
                    return (
                      <div
                        key={s.id}
                        className="border border-novatus rounded-lg overflow-hidden"
                        data-testid={`help-section-${s.id}`}
                      >
                        <button
                          onClick={() => setExpandedId(isOpen ? null : s.id)}
                          className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                          data-testid={`help-section-toggle-${s.id}`}
                        >
                          <span className="text-sm font-semibold text-white">{s.title}</span>
                          <ChevronDown
                            className={`w-4 h-4 text-novatus-text-3 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {isOpen && (
                          <div
                            className="px-4 py-3 text-sm text-novatus-text-2 leading-relaxed handbook-content border-t border-novatus bg-novatus-card-2"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.html) }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="px-5 py-4 border-t border-novatus space-y-2 shrink-0 bg-novatus-bg">
              <button
                onClick={downloadPdf}
                disabled={pdfLoading}
                className="w-full flex items-center justify-center gap-2 bg-novatus-card-2 hover:bg-white/5 border border-novatus-green/50 text-white rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="help-download-pdf-btn"
              >
                {pdfLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-novatus-green" strokeWidth={2.5} />
                )}
                Komplettes Handbuch (PDF) öffnen
              </button>
              <button
                onClick={reopenTour}
                className="w-full flex items-center justify-center gap-2 text-novatus-text-2 hover:text-white rounded-lg py-2 text-xs font-medium transition-colors"
                data-testid="help-reopen-tour-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
                Einführungs-Tour erneut zeigen
              </button>
              <button
                onClick={() => setOpen(false)}
                type="button"
                className="w-full flex items-center justify-center gap-2 bg-novatus-card-2 hover:bg-white/5 border border-novatus text-novatus-text-2 hover:text-white rounded-lg py-3 text-sm font-semibold transition-colors min-h-[44px]"
                data-testid="help-close-bottom-btn"
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpButton;
